import {
  PDFArray,
  PDFDocument,
  PDFName,
  PDFRef,
  StandardFonts,
  rgb,
  type PDFDict,
  type PDFFont,
  type PDFPage,
} from 'pdf-lib';
import { toPdfWinAnsi } from '@/lib/pdf/winAnsi';

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
/** Vertical inset on Clinio's own report pages. Top and bottom stay here. */
const MARGIN_Y = 48;
/** Side inset, about one third of the previous shared margin. */
const MARGIN_X = Math.round(MARGIN_Y / 3);
const BODY_SIZE = 11;
const HEADING_SIZE = 13;
const TITLE_SIZE = 18;
const LINE_GAP = 3;

/** 25 mm. Kept clear for a logo already printed on the paper. */
export const PREPRINTED_TOP_PT = (25 * 72) / 25.4;
/** 20 mm. Kept clear for an address already printed at the bottom. */
export const PREPRINTED_BOTTOM_PT = (20 * 72) / 25.4;

export type PatientReportPdfLetterhead =
  | { mode: 'none' }
  | { mode: 'invoice'; lines: readonly string[]; logo?: Uint8Array | null }
  | { mode: 'image'; bytes: Uint8Array }
  | { mode: 'pdf'; bytes: Uint8Array };

export type PatientReportPdfInput = {
  title: string;
  blocks: readonly {
    heading: string;
    body: string;
    pageBreakAfter?: boolean;
    /** Missing means bold. */
    headingStyle?: 'bold' | 'italic' | 'both';
    /** Contents lines, drawn with each line's heading style instead of `body`. */
    tocLines?: readonly { label: string; style?: 'bold' | 'italic' | 'both' }[];
  }[];
  letterhead: PatientReportPdfLetterhead;
  /** Optional cover sheet. Drawn before the report body. Not stored as patient data. */
  cover?: { title: string; body: string } | null;
};

function wrapLine(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const words = toPdfWinAnsi(text).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(next, size) <= maxWidth) {
      current = next;
      continue;
    }
    if (current) lines.push(current);
    if (font.widthOfTextAtSize(word, size) <= maxWidth) {
      current = word;
      continue;
    }
    let chunk = '';
    for (const ch of word) {
      const attempt = chunk + ch;
      if (font.widthOfTextAtSize(attempt, size) <= maxWidth) {
        chunk = attempt;
      } else {
        if (chunk) lines.push(chunk);
        chunk = ch;
      }
    }
    current = chunk;
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [''];
}

async function embedRaster(pdf: PDFDocument, bytes: Uint8Array) {
  const png = bytes.length > 8 && bytes[0] === 0x89 && bytes[1] === 0x50;
  if (png) return pdf.embedPng(bytes);
  return pdf.embedJpg(bytes);
}

export type LetterheadPainter = {
  /** Points used below the start position, including the gap under the chrome. */
  height: number;
  draw: (page: PDFPage, startY: number) => void;
  /**
   * Full-page PDF stationery. The page already contains the sheet at the
   * file's own resolution. Report text is drawn on top.
   */
  openPage?: () => PDFPage;
};

/** Header artwork stays in this band. A full sheet is not squeezed into it. */
const HEADER_ART_MAX_PT = 160;

/**
 * A full sheet of stationery, as opposed to a logo strip.
 * PDF sizes are points. Image sizes are pixels.
 */
export function isStationerySheet(width: number, height: number, unit: 'pt' | 'px'): boolean {
  if (width <= 0 || height <= 0) return false;
  if (unit === 'pt') return width >= 400 && height >= 560;
  const ratio = height / width;
  return width >= 500 && height >= 700 && ratio >= 1.2 && ratio <= 1.7;
}

/**
 * Fit a whole sheet onto A4. Height 0 keeps the report text in the margins.
 * For a raster, the source size is in pixels. The bitmap is embedded
 * unchanged; only its placement on the page is scaled.
 */
function fitSheetPainter(
  paint: (page: PDFPage, drawWidth: number, drawHeight: number) => void,
  sourceWidth: number,
  sourceHeight: number,
): LetterheadPainter {
  const scale = Math.min(PAGE_WIDTH / sourceWidth, PAGE_HEIGHT / sourceHeight);
  const drawWidth = sourceWidth * scale;
  const drawHeight = sourceHeight * scale;
  return {
    height: 0,
    draw: (page) => {
      paint(page, drawWidth, drawHeight);
    },
  };
}

const NEAR_PT = 0.5;

function near(value: number, target: number): boolean {
  return Math.abs(value - target) < NEAR_PT;
}

/**
 * Move a copied PDF sheet onto A4 without resampling it.
 * An A4 file is left untouched. A coordinate scale is used only when the
 * page would otherwise be clipped. Images and vectors keep their own data.
 */
function fitCopiedSheet(page: PDFPage): void {
  const box = page.getMediaBox();
  const scale = Math.min(PAGE_WIDTH / box.width, PAGE_HEIGHT / box.height);
  const drawWidth = box.width * scale;
  const drawHeight = box.height * scale;
  const dx = (PAGE_WIDTH - drawWidth) / 2 - box.x * scale;
  const dy = (PAGE_HEIGHT - drawHeight) / 2 - box.y * scale;
  if (!near(scale, 1)) page.scaleContent(scale, scale);
  if (!near(dx, 0) || !near(dy, 0)) page.translateContent(dx, dy);
  else if (near(scale, 1)) page.node.normalize();
}

function contentStreamRefs(page: PDFPage): PDFRef[] {
  const contents = page.node.Contents();
  if (contents instanceof PDFArray) {
    const refs: PDFRef[] = [];
    for (let index = 0; index < contents.size(); index += 1) {
      const value = contents.get(index);
      if (value instanceof PDFRef) refs.push(value);
    }
    return refs;
  }
  const raw = page.node.get(PDFName.of('Contents'));
  return raw instanceof PDFRef ? [raw] : [];
}

function openSheetPage(pdf: PDFDocument, resources: PDFDict, refs: readonly PDFRef[]): PDFPage {
  const page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  page.node.set(PDFName.of('Resources'), resources);
  for (const ref of refs) page.node.addContentStream(ref);
  return page;
}

/**
 * Copy the letterhead page into this document and reuse its streams.
 * embedPdf would wrap the page in a Form XObject. Preview and print then
 * rasterize that form, which is what makes a logo look soft.
 */
async function preparePdfSheet(
  pdf: PDFDocument,
  src: PDFDocument,
): Promise<LetterheadPainter | null> {
  const [template] = await pdf.copyPages(src, [0]);
  if (!template || template.getRotation().angle % 360 !== 0) return null;
  fitCopiedSheet(template);
  const refs = contentStreamRefs(template);
  const resources = template.node.Resources();
  if (!resources || refs.length === 0) return null;
  // The copy is only a carrier for the streams. Leaving it in the file would
  // add a page that is not part of the report.
  pdf.context.delete(template.ref);
  return {
    height: 0,
    draw: () => undefined,
    openPage: () => openSheetPage(pdf, resources, refs),
  };
}

/**
 * Letterhead is page chrome. The same painter is used on every report page.
 * A cover sheet is a separate page and does not use this painter.
 * A page-sized file is the sheet itself and is drawn at page size. A small
 * logo stays in the top band and pushes the text down.
 */
export async function prepareLetterhead(
  pdf: PDFDocument,
  font: PDFFont,
  letterhead: PatientReportPdfLetterhead,
): Promise<LetterheadPainter> {
  const empty: LetterheadPainter = { height: 0, draw: () => undefined };
  if (letterhead.mode === 'none') return empty;
  const width = PAGE_WIDTH - MARGIN_X * 2;

  if (letterhead.mode === 'pdf') {
    try {
      const src = await PDFDocument.load(letterhead.bytes);
      const srcPage = src.getPages()[0];
      if (!srcPage) return empty;
      const srcSize = srcPage.getSize();
      const sheet = isStationerySheet(srcSize.width, srcSize.height, 'pt');
      if (sheet) {
        try {
          const painted = await preparePdfSheet(pdf, src);
          if (painted) return painted;
        } catch {
          // The sheet is still drawn below, as a form, if the copy failed.
        }
      }
      const [embedded] = await pdf.embedPdf(src, [0]);
      if (sheet || isStationerySheet(embedded.width, embedded.height, 'pt')) {
        return fitSheetPainter(
          (page, drawWidth, drawHeight) => {
            page.drawPage(embedded, {
              x: (PAGE_WIDTH - drawWidth) / 2,
              y: (PAGE_HEIGHT - drawHeight) / 2,
              width: drawWidth,
              height: drawHeight,
            });
          },
          embedded.width,
          embedded.height,
        );
      }
      const scale = Math.min(PAGE_WIDTH / embedded.width, HEADER_ART_MAX_PT / embedded.height, 1);
      const drawWidth = embedded.width * scale;
      const drawHeight = embedded.height * scale;
      const height = drawHeight + 16;
      return {
        height,
        draw: (page, startY) => {
          page.drawPage(embedded, {
            x: (PAGE_WIDTH - drawWidth) / 2,
            y: startY - drawHeight,
            width: drawWidth,
            height: drawHeight,
          });
        },
      };
    } catch {
      return empty;
    }
  }

  let image: Awaited<ReturnType<PDFDocument['embedPng']>> | null = null;
  let imageWidth = 0;
  let imageHeight = 0;
  const imageBytes =
    letterhead.mode === 'image'
      ? letterhead.bytes
      : letterhead.mode === 'invoice'
        ? letterhead.logo
        : null;
  if (imageBytes && imageBytes.length > 0) {
    try {
      image = await embedRaster(pdf, imageBytes);
      if (
        image &&
        letterhead.mode === 'image' &&
        isStationerySheet(image.width, image.height, 'px')
      ) {
        const painted = image;
        return fitSheetPainter(
          (page, drawWidth, drawHeight) => {
            page.drawImage(painted, {
              x: (PAGE_WIDTH - drawWidth) / 2,
              y: (PAGE_HEIGHT - drawHeight) / 2,
              width: drawWidth,
              height: drawHeight,
            });
          },
          painted.width,
          painted.height,
        );
      }
      if (image) {
        const maxHeight = letterhead.mode === 'image' ? 120 : 56;
        const scale = Math.min(width / image.width, maxHeight / image.height, 1);
        imageWidth = image.width * scale;
        imageHeight = image.height * scale;
      }
    } catch {
      image = null;
    }
  }

  const invoiceLines =
    letterhead.mode === 'invoice'
      ? letterhead.lines.flatMap((line) => wrapLine(line, font, 10, width))
      : [];
  let height = 0;
  if (image) height += imageHeight + 10;
  height += invoiceLines.length * 13;
  if (letterhead.mode === 'invoice' && (invoiceLines.length > 0 || image)) height += 8;
  if (height === 0) return empty;

  return {
    height,
    draw: (page, startY) => {
      let y = startY;
      if (image) {
        page.drawImage(image, {
          x: MARGIN_X,
          y: y - imageHeight,
          width: imageWidth,
          height: imageHeight,
        });
        y -= imageHeight + 10;
      }
      for (const line of invoiceLines) {
        page.drawText(line, {
          x: MARGIN_X,
          y: y - 10,
          size: 10,
          font,
          color: rgb(0.2, 0.24, 0.28),
        });
        y -= 13;
      }
    },
  };
}

/** A4 PDF. Cover text is its own front sheet. Letterhead repeats on the report pages. */
export async function buildPatientReportPdf(input: PatientReportPdfInput): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const italic = await pdf.embedFont(StandardFonts.HelveticaOblique);
  const boldItalic = await pdf.embedFont(StandardFonts.HelveticaBoldOblique);
  const headingFont = (style: 'bold' | 'italic' | 'both' | undefined) => {
    if (style === 'italic') return italic;
    if (style === 'both') return boldItalic;
    return bold;
  };
  const painter = await prepareLetterhead(pdf, font, input.letterhead);
  const width = PAGE_WIDTH - MARGIN_X * 2;

  let page: PDFPage | null = null;
  let y = 0;
  let mode: 'cover' | 'body' = 'body';
  let openedAt = Number.NaN;

  const open = (next: 'cover' | 'body') => {
    mode = next;
    if (next === 'body' && painter.openPage) {
      page = painter.openPage();
    } else {
      page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      if (next === 'body') painter.draw(page, PAGE_HEIGHT - MARGIN_Y);
    }
    if (next === 'body') {
      const height = Math.min(painter.height, 220);
      const topClear = Math.max(MARGIN_Y, PREPRINTED_TOP_PT);
      const belowChrome = PAGE_HEIGHT - MARGIN_Y - height;
      y = Math.min(belowChrome, PAGE_HEIGHT - topClear);
    } else {
      y = PAGE_HEIGHT - MARGIN_Y;
    }
    openedAt = y;
  };

  const ensure = (needed: number) => {
    const floor = mode === 'body' ? Math.max(MARGIN_Y, PREPRINTED_BOTTOM_PT) : MARGIN_Y;
    if (page && y - needed >= floor) return;
    if (page && y === openedAt) return;
    open(mode);
  };

  const drawLines = (lines: string[], usedFont: PDFFont, size: number) => {
    const step = size + LINE_GAP;
    for (const line of lines) {
      ensure(step);
      if (!page) return;
      page.drawText(line, {
        x: MARGIN_X,
        y: y - size,
        size,
        font: usedFont,
        color: rgb(0.12, 0.14, 0.16),
      });
      y -= step;
    }
  };

  const cover = input.cover;
  if (cover && (cover.title.trim() || cover.body.trim())) {
    mode = 'cover';
    if (cover.title.trim()) {
      drawLines(wrapLine(cover.title.trim(), bold, TITLE_SIZE, width), bold, TITLE_SIZE);
      y -= 8;
    }
    for (const paragraph of cover.body.split(/\n/)) {
      drawLines(wrapLine(paragraph, font, BODY_SIZE, width), font, BODY_SIZE);
    }
    page = null;
  }

  mode = 'body';
  drawLines(wrapLine(input.title || 'Rapport', bold, TITLE_SIZE, width), bold, TITLE_SIZE);
  y -= 8;

  const blocks = input.blocks.filter((block) => block.heading.trim() || block.body.trim());
  if (blocks.length === 0) {
    drawLines(wrapLine(' ', font, BODY_SIZE, width), font, BODY_SIZE);
  }
  let breakBefore = false;
  for (const block of blocks) {
    if (breakBefore) page = null;
    if (block.heading.trim()) {
      y -= 6;
      const used = headingFont(block.headingStyle);
      drawLines(wrapLine(block.heading.trim(), used, HEADING_SIZE, width), used, HEADING_SIZE);
    }
    if (block.tocLines && block.tocLines.length > 0) {
      for (const line of block.tocLines) {
        drawLines(wrapLine(line.label, font, BODY_SIZE, width), font, BODY_SIZE);
      }
    } else {
      for (const paragraph of (block.body || '').split(/\n/)) {
        drawLines(wrapLine(paragraph, font, BODY_SIZE, width), font, BODY_SIZE);
      }
    }
    breakBefore = block.pageBreakAfter === true;
  }

  if (pdf.getPageCount() === 0) open('body');
  return pdf.save();
}

export function invoiceLetterheadLines(input: {
  name?: string | null;
  address?: unknown;
  cvr?: string | null;
  phone?: string | null;
  email?: string | null;
}): string[] {
  const address = addressRecord(input.address);
  const street = text(address?.street) ?? text(address?.line1);
  const zip = text(address?.zip_code) ?? text(address?.zip) ?? text(address?.postal_code);
  const city = text(address?.city);
  const country = text(address?.country);
  const lines = [
    text(input.name),
    street,
    [zip, city].filter(Boolean).join(' ') || null,
    country,
    input.cvr ? `CVR ${input.cvr}` : null,
    text(input.phone),
    text(input.email),
  ];
  return lines.filter((line): line is string => Boolean(line));
}

function addressRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
